import { Helmet } from 'react-helmet-async';
import usePrerenderReady from '../hooks/usePrerenderReady';
import { SITE_URL } from '../seoConfig';
import { buildArticleSchema, buildBreadcrumbSchema, toJsonLd } from '../schema';

const WAIVER_WIRE_TITLE = 'Fantasy Football Waiver Wire Strategy — RosterRx';
const WAIVER_WIRE_DESCRIPTION =
  'How to prioritize waiver wire and free agent pickups in fantasy football: what to look for, how much priority or FAAB budget to spend, and common mistakes to avoid.';
// Update by hand only when this page's advice actually changes -- not the build date.
const WAIVER_WIRE_LAST_UPDATED = '2026-07-27';
const ARTICLE_SCHEMA = buildArticleSchema({
  path: '/waiver-wire-strategy',
  title: WAIVER_WIRE_TITLE,
  description: WAIVER_WIRE_DESCRIPTION,
  datePublished: WAIVER_WIRE_LAST_UPDATED,
  dateModified: WAIVER_WIRE_LAST_UPDATED,
});
const BREADCRUMB_SCHEMA = buildBreadcrumbSchema({
  path: '/waiver-wire-strategy',
  title: 'Waiver Wire Strategy',
});

/**
 * Target query/intent: "fantasy football waiver wire strategy". First entry in the "ongoing
 * content" category (R2.3) -- an evergreen strategy guide rather than a dated, week-specific post,
 * since week-specific advice needs a live data source RosterRx doesn't have wired to a public page
 * yet. Proves out the pattern (own route, own Helmet, added to publicRoutes.js/llms.txt) for
 * future additions -- dated posts, more strategy guides, etc. -- without committing to a
 * maintenance cadence this pass.
 */
function WaiverWireStrategyView() {
  usePrerenderReady();

  return (
    <div>
      <Helmet>
        <title>{WAIVER_WIRE_TITLE}</title>
        <meta name='description' content={WAIVER_WIRE_DESCRIPTION} />
        <link rel='canonical' href={`${SITE_URL}/waiver-wire-strategy`} />
        <meta property='og:title' content={WAIVER_WIRE_TITLE} />
        <meta property='og:description' content={WAIVER_WIRE_DESCRIPTION} />
        <meta property='og:url' content={`${SITE_URL}/waiver-wire-strategy`} />
        <meta property='og:type' content='article' />
        <meta name='twitter:card' content='summary' />
        <meta name='twitter:title' content={WAIVER_WIRE_TITLE} />
        <meta name='twitter:description' content={WAIVER_WIRE_DESCRIPTION} />
        <script type='application/ld+json'>{toJsonLd(ARTICLE_SCHEMA)}</script>
        <script type='application/ld+json'>{toJsonLd(BREADCRUMB_SCHEMA)}</script>
      </Helmet>
      <div className='card'>
        <h2 style={{ marginTop: 0 }}>Fantasy Football Waiver Wire Strategy</h2>
        <p>
          The waiver wire is the fastest way to improve a fantasy roster after the draft. The
          short version: prioritize players who are about to see more opportunity (a starting job,
          more touches, more targets) over players who just had one good game, and don't be afraid
          to drop a struggling bench player to make room.
        </p>
      </div>

      <div className='card'>
        <h2 style={{ marginTop: 0 }}>What to prioritize</h2>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 8 }}>
            <strong>Opportunity over one-week production.</strong> A backup who just inherited a
            starting job is usually worth more than a starter who had one big game — opportunity
            tends to repeat, a single good game often doesn't.
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong>Target share and touches, not just touchdowns.</strong> Touchdowns vary
            week to week; players who are consistently getting the ball are more likely to
            produce again.
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong>Your bye week and matchup schedule.</strong> A solid streaming option who
            covers a bye week for one of your studs is often worth more short-term than a
            speculative long-shot add.
          </li>
          <li>
            <strong>Don't hoard.</strong> If your bench is full of players who haven't produced in
            weeks, dropping one for a real opportunity is almost always the right move.
          </li>
        </ul>
      </div>

      <div className='card'>
        <h2 style={{ marginTop: 0 }}>Common mistakes</h2>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 8 }}>
            Spending your entire FAAB budget or your #1 waiver priority on a name you recognize,
            rather than the player with the clearest path to touches.
          </li>
          <li style={{ marginBottom: 8 }}>
            Dropping a rostered player right before their bye week ends, instead of just riding out
            one bad week.
          </li>
          <li>
            Chasing last week's box score instead of this week's opportunity — a receiver who
            scored twice on a fluke play is a different bet than one who's suddenly running more
            routes.
          </li>
        </ul>
      </div>
    </div>
  );
}

export default WaiverWireStrategyView;
